import { defineStore } from 'pinia';
import { DialogService } from '@/services/dialogs';

interface DialogsState {
  /** List of dialogs for the current bot; false while not loaded/cleared */
  dialogs: any[] | false;
  /** The currently loaded chat (message history); false while not loaded/cleared */
  chat: any | false;
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/dialogs.ts` (namespace `dialogs`).
 * In-memory only — no `vuexPersist` option (legacy module was never persisted).
 *
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - Legacy had a state prop `dialogs` and an identity-passthrough getter of the same name
 *   (`getters.dialogs(state) => state.dialogs`). State keeps the bare name; the getter is renamed
 *   `dialogsGetter`.
 * - Legacy had a state prop `chat` and an identity-passthrough getter of the same name
 *   (`getters.chat(state) => state.chat`). State keeps the bare name; the getter is renamed
 *   `chatGetter`.
 * - Legacy also had an action `chat` (fetches + commits the chat), which collides with the state
 *   prop `chat` (state wins the bare name, by the same precedence as the state/getter rule above).
 *   The action is renamed `chatAction`.
 * - Mutations `setDialogs`/`setChat` had no same-named actions, so they become same-named actions
 *   `setDialogs`/`setChat`.
 */
export const useDialogsStore = defineStore('dialogs', {
  state: (): DialogsState => ({
    dialogs: false,
    chat: false,
  }),
  getters: {
    /**
     * Renamed from legacy getter `dialogs` (identity passthrough of state; collides with the
     * `dialogs` state prop — state keeps the bare name). Consumers can also just read
     * `useDialogsStore().dialogs` directly.
     */
    dialogsGetter(state): any[] | false {
      return state.dialogs;
    },
    /**
     * Renamed from legacy getter `chat` (identity passthrough of state; collides with the `chat`
     * state prop — state keeps the bare name). Consumers can also just read
     * `useDialogsStore().chat` directly.
     */
    chatGetter(state): any | false {
      return state.chat;
    },
  },
  actions: {
    /**
     * Converted from legacy action `get`. Loads the list of dialogs for a bot.
     */
    async get(uniqueBotId: string): Promise<boolean> {
      const result: any = await DialogService.getDialogs(uniqueBotId);
      this.setDialogs(result.dialogs);
      return true;
    },
    /**
     * Renamed from legacy action `chat` (collides with the `chat` state prop — state keeps the
     * bare name). Loads the chat (message history) for a given user/bot pair.
     */
    async chatAction({
      uniqueUserId,
      uniqueBotId,
    }: {
      uniqueUserId: string;
      uniqueBotId: string;
    }): Promise<boolean> {
      const uuid = uniqueUserId.replace(/%C3%B6/g, 'ö').replace(/�/g, 'ö');
      const result: any = await DialogService.getChat(uuid, uniqueBotId);
      this.setChat(result.chat);
      return true;
    },
    /**
     * Converted from legacy action `clearChat`.
     */
    clearChat() {
      this.setChat(false);
    },
    /**
     * Converted from legacy action `clear`.
     */
    clear() {
      this.setDialogs(false);
    },
    /**
     * Converted from legacy mutation `setDialogs` (no matching legacy action existed).
     */
    setDialogs(dialogs: any[] | false) {
      this.dialogs = dialogs;
    },
    /**
     * Converted from legacy mutation `setChat` (no matching legacy action existed).
     */
    setChat(chat: any | false) {
      this.chat = chat;
    },
  },
});
